<template>
  <div>
    <v-select
      v-model="model"
      :options="formattedOptions"
      :filterable="false"
      :disabled="disabled"
      @search="onSearch"
      :multiple="(multiple || multiple === 1)"
      :clearable="(multiple || multiple === 1)"
      ref="selectable"
      @input="onInput"
      label="label"
      @option:selected="emt => $emit('option:selected', emt)"
      :placeholder="placeholder"
    >
      <template #list-footer>
        <li class="pagination">
          <b-button
            variant="outline-primary"
            size="sm"
            :disabled="!hasPrevPage"
            @click="prevPage"
          >
            <feather-icon icon="ChevronLeftIcon" size="18" />
          </b-button>
          <b-button
            variant="outline-primary"
            size="sm"
            :disabled="!hasNextPage"
            @click="nextPage"
          >
            <feather-icon icon="ChevronRightIcon" size="18" />
          </b-button>
        </li>
      </template>
    </v-select>
  </div>
</template>

<script>
import vSelect from "vue-select";
import { debounce } from "lodash";
import { BButton } from "bootstrap-vue";
import axios from "@axios";

export default {
  name: "UsersSelectPaginated",
  components: {
    vSelect,
    BButton,
  },
  props: {
    value: {
      type: [Array, Object, Number, String, null],
      default: null,
    },
    multiple: {
      type: [Boolean, Number],
      default: false,
    },
    endpoint: {
      type: String,
      default: "/v1/users",
    },
    labelKey: {
      type: String,
      default: "name",
    },
    placeholder: {
      type: String,
      default: "Buscar usuario...",
    },
    extraParams: {
      type: Object,
      default: () => ({}),
    },
    showEmailInLabel: {
      type: Boolean,
      default: true,
    },
    limit: {
      type: Number,
      default: 10,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      model: null,
      search: "",
      currentPage: 1,
      meta: null,
      options: [],
    };
  },
  created() {
    this.model = this.value || null;
  },
  mounted() {
    this.fetchData();
  },
  watch: {
    currentPage() {
      this.fetchData();
    },
    value(newVal) {
      this.model = newVal;
    },
  },
  computed: {
    formattedOptions() {
      return (this.options || []).map((item) => {
        const label =
          item.full_name ||
          item[this.labelKey] ||
          item.name ||
          item.email ||
          `${item.first_name || ""} ${item.last_name || ""}`.trim() ||
          `User #${item.id}`;

        const suffix = this.showEmailInLabel
          ? item.email
          : (item.username || item.user);
        const displayLabel = suffix ? `${label} | ${suffix}` : label;

        return {
          ...item,
          value: item.id,
          label: displayLabel,
        };
      });
    },
    hasNextPage() {
      if (this.meta) {
        return this.meta.current_page < this.meta.last_page;
      }
      return false;
    },
    hasPrevPage() {
      if (this.meta) {
        return this.meta.current_page - 1 > 0;
      }
      return false;
    },
  },
  methods: {
    onInput() {
      this.$emit("input", this.model || (this.multiple ? [] : null));
    },
    fetchData() {
      return new Promise((resolve, reject) => {
        axios
          .get(this.endpoint, {
            params: {
              q: this.search,
              max: this.limit || 10,
              "page[n]": this.currentPage,
              sortBy: "name",
              sortDesc: false,
              ...this.extraParams,
            },
          })
          .then(({ data }) => {
            this.meta = data.meta;
            this.options = data.data || [];
            resolve();
          })
          .catch((error) => {
            reject();
            console.log("UsersSelectPaginated: request error", error.response);
          });
      });
    },
    onSearch: debounce(async function (query, loading) {
      if (query && query.length) {
        loading(true);
        this.search = query;
        this.currentPage = 1;
        await this.fetchData();
        loading(false);
      } else {
        this.currentPage = 1;
        this.search = "";
        await this.fetchData();
      }
    }, 500),
    nextPage() {
      if (this.hasNextPage) {
        this.currentPage += 1;
      }
    },
    prevPage() {
      if (this.hasPrevPage) {
        this.currentPage -= 1;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.pagination {
  display: flex;
  justify-content: space-around;
  margin: 0.25rem 0.25rem 0;
}

.pagination button {
  flex-grow: 1;

  &:first-child {
    margin-right: 5px;
  }
}

.pagination button:hover {
  cursor: pointer;
}
</style>
